import Cookies from 'js-cookie';
import { NavigateFunction } from 'react-router-dom';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';

import { AsyncState } from 'shared/libs/state';
import { FormControl, FormGroup } from 'shared/libs/form';
import { HttpClient } from 'shared/libs/http';
import { LoadPlanEditResponse, PlanEditFormDto, PlanLimitValue, PlanOptionMap, UpdatePlanResponse } from './types';

type FeatureLimitKey = 'job_post' | 'resume_download' | 'contact_view' | 'profile_view' | 'job_apply';
type FeatureKey = FeatureLimitKey | 'candidate_search';
type ErrorKey =
   | keyof Pick<
        PlanEditFormDto,
        'plan_name' | 'full_plan_name' | 'amount' | 'planuser' | 'type' | 'type_value'
     >
   | FeatureLimitKey
   | 'job_apply_checkbx'
   | 'job_apply_input'
   | 'emp_checkbx';

const EMPTY_FEATURE_VALUE = 'empty';
const FEATURE_UNLIMITED_VALUE: Record<FeatureLimitKey, string> = {
   job_post: '50000',
   resume_download: '1000000',
   contact_view: '1000000',
   profile_view: '1000000',
   job_apply: '1000000',
};

export class PlanEditStore {
   readonly loadPlanState = new AsyncState<LoadPlanEditResponse>();
   readonly updatePlanState = new AsyncState<UpdatePlanResponse>();

   readonly formGroup = new FormGroup({
      plan_name: new FormControl<string>(''),
      full_plan_name: new FormControl<string>(''),
      type: new FormControl<string>(''),
      planuser: new FormControl<string>(''),
      type_value: new FormControl<string>(''),
      amount: new FormControl<string>(''),
   });

   planTypeOptions: PlanOptionMap = {};
   userTypeOptions: PlanOptionMap = {};
   errors: Partial<Record<ErrorKey, string>> = {};
   private loadedPlan: Partial<PlanEditFormDto> = {};

   job_post: PlanLimitValue = EMPTY_FEATURE_VALUE;
   resume_download: PlanLimitValue = EMPTY_FEATURE_VALUE;
   contact_view: PlanLimitValue = EMPTY_FEATURE_VALUE;
   candidate_search: PlanLimitValue = EMPTY_FEATURE_VALUE;
   profile_view: PlanLimitValue = EMPTY_FEATURE_VALUE;
   job_apply: PlanLimitValue = EMPTY_FEATURE_VALUE;

   constructor(
      private readonly slug: string,
      private readonly navigate: NavigateFunction
   ) {
      makeObservable<this, 'loadedPlan'>(this, {
         planTypeOptions: observable,
         userTypeOptions: observable,
         errors: observable,
         loadedPlan: observable,
         job_post: observable,
         resume_download: observable,
         contact_view: observable,
         candidate_search: observable,
         profile_view: observable,
         job_apply: observable,
         isLoading: computed,
         isSaving: computed,
         isEmployerPlan: computed,
         isJobseekerPlan: computed,
         timePeriodLabel: computed,
         hasJobPost: computed,
         hasResumeDownload: computed,
         hasContactView: computed,
         hasCandidateSearch: computed,
         hasProfileView: computed,
         hasJobApply: computed,
         submit: action.bound,
         reset: action.bound,
         setBaseField: action.bound,
         toggleFeature: action.bound,
         toggleUnlimited: action.bound,
         setLimitValue: action.bound,
         validate: action.bound,
      });
   }

   get isLoading(): boolean {
      return this.loadPlanState.status === 'idle' || this.loadPlanState.status === 'pending';
   }

   get isSaving(): boolean {
      return this.updatePlanState.status === 'pending';
   }

   get isEmployerPlan(): boolean {
      return this.formGroup.controls.planuser.value === 'employer';
   }

   get isJobseekerPlan(): boolean {
      return this.formGroup.controls.planuser.value === 'jobseeker';
   }

   get timePeriodLabel(): string {
      if (this.formGroup.controls.type.value === 'Years') {
         return 'Time Period (In Years)';
      }
      if (this.formGroup.controls.type.value === 'Months') {
         return 'Time Period (In Months)';
      }
      return 'Time Period';
   }

   get hasJobPost(): boolean {
      return this.isFeatureSelected('job_post');
   }

   get hasResumeDownload(): boolean {
      return this.isFeatureSelected('resume_download');
   }

   get hasContactView(): boolean {
      return this.isFeatureSelected('contact_view');
   }

   get hasCandidateSearch(): boolean {
      return this.isFeatureSelected('candidate_search');
   }

   get hasProfileView(): boolean {
      return this.isFeatureSelected('profile_view');
   }

   get hasJobApply(): boolean {
      return this.isFeatureSelected('job_apply');
   }

   async loadPlan(): Promise<void> {
      const tokenKey = Cookies.get('token');
      const adminID = Cookies.get('adminID');

      if (!tokenKey) {
         this.navigate('/admin');
         return;
      }

      await this.loadPlanState.run(
         () =>
            HttpClient.post<LoadPlanEditResponse>(`/admin/plan/editPlan/${this.slug}`, null, {
               headers: {
                  adminid: adminID,
                  Authorization: `Bearer ${tokenKey}`,
               },
            }),
         {
            onSuccess: (resp) => {
               runInAction(() => {
                  this.hydrate(resp.response.plan);
                  this.planTypeOptions = resp.response.planType ?? {};
                  this.userTypeOptions = resp.response.userType ?? {};
               });
            },
         }
      );
   }

   setBaseField(
      field: keyof typeof this.formGroup.controls,
      value: string
   ): void {
      this.formGroup.controls[field].setValue(value);
      this.clearError(field);

      if (field === 'planuser') {
         this.clearError('emp_checkbx');
         this.clearError('job_apply_checkbx');

         if (value === 'employer') {
            this.job_apply = '0';
         }

         if (value === 'jobseeker') {
            this.job_post = '0';
            this.resume_download = '0';
            this.contact_view = '0';
            this.candidate_search = '0';
            this.profile_view = '0';
         }
      }
   }

   toggleFeature(feature: FeatureKey, checked: boolean): void {
      this[feature] = checked ? this.defaultSelectedFeatureValue(feature) : EMPTY_FEATURE_VALUE;
      this.clearError('emp_checkbx');
      this.clearError('job_apply_checkbx');
      if (feature !== 'candidate_search') {
         this.clearError(feature === 'job_apply' ? 'job_apply_input' : feature);
      }
   }

   toggleUnlimited(feature: FeatureLimitKey, checked: boolean): void {
      this[feature] = checked ? FEATURE_UNLIMITED_VALUE[feature] : '';
      this.clearError(feature === 'job_apply' ? 'job_apply_input' : feature);
   }

   setLimitValue(feature: FeatureLimitKey, value: string): void {
      if (this.isUnlimited(feature)) {
         return;
      }

      if (value === '' || Number(value) >= 0) {
         this[feature] = value;
      }

      this.clearError(feature === 'job_apply' ? 'job_apply_input' : feature);
   }

   isUnlimited(feature: FeatureLimitKey): boolean {
      const value = String(this[feature]);
      return value === FEATURE_UNLIMITED_VALUE[feature];
   }

   reset(): void {
      this.hydrate(this.loadedPlan as PlanEditFormDto);
      this.errors = {};
   }

   async submit({
      onSuccess,
      onError,
   }: {
      onSuccess?: (resp: UpdatePlanResponse) => void;
      onError?: (message: string) => void;
   } = {}): Promise<void> {
      if (!this.validate()) {
         return;
      }

      const tokenKey = Cookies.get('token');
      const adminID = Cookies.get('adminID');

      if (!tokenKey) {
         this.navigate('/admin');
         return;
      }

      await this.updatePlanState.run(
         () =>
            HttpClient.post<UpdatePlanResponse>(`/admin/plan/editPlan/${this.slug}`, this.buildPayload(), {
               headers: {
                  adminid: adminID,
                  Authorization: `Bearer ${tokenKey}`,
               },
            }),
         {
            onSuccess: (resp) => {
               if (resp.status === 200) {
                  onSuccess?.(resp);
                  window.scrollTo(0, 0);
                  this.navigate('/admin/manage-plans');
               } else {
                  onError?.(resp.message || 'Could not update plan');
               }
            },
            onError: (error) => {
               onError?.(error.message || 'Could not update plan');
            },
         }
      );
   }

   private hydrate(plan: PlanEditFormDto): void {
      this.loadedPlan = { ...plan };

      this.formGroup.patchValue({
         plan_name: this.toInputValue(plan.plan_name),
         full_plan_name: this.toInputValue(plan.full_plan_name),
         type: this.toInputValue(plan.type),
         planuser: this.toInputValue(plan.planuser),
         type_value: this.toInputValue(plan.type_value),
         amount: this.toInputValue(plan.amount),
      });

      this.job_post = this.normalizeFeatureValue(plan.job_post);
      this.resume_download = this.normalizeFeatureValue(plan.resume_download);
      this.contact_view = this.normalizeFeatureValue(plan.contact_view);
      this.candidate_search = this.normalizeFeatureValue(plan.candidate_search);
      this.profile_view = this.normalizeFeatureValue(plan.profile_view);
      this.job_apply = this.normalizeFeatureValue(plan.job_apply);
   }

   validate(): boolean {
      const nextErrors: Partial<Record<ErrorKey, string>> = {};
      const value = this.formGroup.value;

      if (!value.plan_name.trim()) {
         nextErrors.plan_name = 'Plan Name is required';
      }

      if (!value.amount.trim()) {
         nextErrors.amount = 'Amount is required';
      }

      if (!value.planuser.trim()) {
         nextErrors.planuser = 'User Plan is required';
      }

      if (!value.type.trim()) {
         nextErrors.type = 'Plan Type is required';
      }

      if (!value.type_value.trim()) {
         nextErrors.type_value = 'Type is required';
      }

      if (value.planuser === 'jobseeker') {
         if (!this.hasJobApply) {
            nextErrors.job_apply_checkbx = 'Please select atleast one feature';
         } else {
            this.validateLimit('job_apply', 'job_apply_input', nextErrors);
         }
      }

      if (value.planuser === 'employer') {
         if (
            !(
               this.hasJobPost ||
               this.hasResumeDownload ||
               this.hasContactView ||
               this.hasCandidateSearch ||
               this.hasProfileView
            )
         ) {
            nextErrors.emp_checkbx = 'Please select at least one feature';
         }

         if (this.hasJobPost) {
            this.validateLimit('job_post', 'job_post', nextErrors);
         }

         if (this.hasResumeDownload) {
            this.validateLimit('resume_download', 'resume_download', nextErrors);
         }

         if (this.hasContactView) {
            this.validateLimit('contact_view', 'contact_view', nextErrors);
         }

         if (this.hasProfileView) {
            this.validateLimit('profile_view', 'profile_view', nextErrors);
         }
      }

      this.errors = nextErrors;
      return Object.keys(nextErrors).length === 0;
   }

   private validateLimit(
      feature: FeatureLimitKey,
      errorKey: ErrorKey,
      nextErrors: Partial<Record<ErrorKey, string>>
   ): void {
      const value = this[feature];

      if (value === '') {
         nextErrors[errorKey] = 'Please enter a value';
         return;
      }

      if (Number(value) <= 0) {
         nextErrors[errorKey] = 'Please enter a value greater than 0';
      }
   }

   private buildPayload(): PlanEditFormDto {
      return {
         ...this.loadedPlan,
         plan_name: this.formGroup.controls.plan_name.value.trim(),
         full_plan_name: this.toNullableString(this.formGroup.controls.full_plan_name.value),
         type: this.formGroup.controls.type.value,
         planuser: this.formGroup.controls.planuser.value,
         type_value: this.formGroup.controls.type_value.value.trim(),
         amount: this.formGroup.controls.amount.value.trim(),
         job_post: this.job_post,
         resume_download: this.resume_download,
         contact_view: this.contact_view,
         candidate_search: this.candidate_search,
         job_apply: this.job_apply,
         profile_view: this.profile_view,
      };
   }

   private isFeatureSelected(feature: FeatureKey): boolean {
      return String(this[feature]) !== EMPTY_FEATURE_VALUE;
   }

   private defaultSelectedFeatureValue(feature: FeatureKey): PlanLimitValue {
      return feature === 'candidate_search' ? '1' : '';
   }

   private clearError(key: ErrorKey): void {
      if (this.errors[key]) {
         this.errors = {
            ...this.errors,
            [key]: '',
         };
      }
   }

   private normalizeFeatureValue(value: PlanLimitValue | undefined): PlanLimitValue {
      if (value === undefined || value === null) {
         return EMPTY_FEATURE_VALUE;
      }
      return value;
   }

   private toInputValue(value: PlanLimitValue | null | undefined): string {
      if (value === undefined || value === null) {
         return '';
      }
      return String(value);
   }

   private toNullableString(value: string): string | null {
      const trimmedValue = value.trim();
      return trimmedValue ? trimmedValue : null;
   }
}
